import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Sparkles, Copy, Check, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function AIAnalysis({ module, contextData, disabled = false }) {
  const [analysis, setAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleAnalyze = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/analyze`, {
        module,
        context: contextData
      });
      
      if (response.data.success) {
        setAnalysis(response.data.analysis);
      } else {
        setError('No se pudo completar el análisis. Por favor intenta de nuevo.');
      }
    } catch (err) {
      console.error('AI Analysis error:', err);
      setError('Error de conexión. Por favor verifica tu conexión e intenta de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    if (analysis) {
      try {
        await navigator.clipboard.writeText(analysis);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Fallback for environments where clipboard API is not available
        const textArea = document.createElement('textarea');
        textArea.value = analysis;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (e) {
          console.error('Copy failed:', e);
        }
        document.body.removeChild(textArea);
      }
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-8"
      data-testid="ai-analysis-section"
    >
      {/* Trigger Button */}
      <Button
        onClick={handleAnalyze}
        disabled={disabled || isLoading}
        className="w-full gap-3 bg-gradient-to-r from-[#1A56DB] to-[#3B82F6] hover:from-[#1548c2] hover:to-[#2563eb] text-white py-6 rounded-xl shadow-lg shadow-blue-500/25 font-semibold text-base"
        data-testid="analyze-button"
      >
        {isLoading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Analizando con IA...
          </>
        ) : (
          <>
            <Sparkles className="h-5 w-5" />
            Analizar con IA
          </>
        )}
      </Button>

      {/* Analysis Result */}
      <AnimatePresence mode="wait">
        {analysis && !isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-6 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border-l-4 border-[#1A56DB] overflow-hidden"
            data-testid="ai-analysis-result"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-[#1A56DB]/10">
                    <Bot className="h-5 w-5 text-[#1A56DB]" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900">Diagnóstico IA</h4>
                    <p className="text-xs text-slate-500">Powered by Claude</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  className="gap-2 text-slate-600 hover:text-slate-900"
                  data-testid="copy-analysis-button"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 text-emerald-500" />
                      Copiado
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" />
                      Copiar
                    </>
                  )}
                </Button>
              </div>
              
              {/* Analysis Content */}
              <div className="prose prose-sm prose-slate max-w-none">
                <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                  {analysis}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {error && !isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-6 p-4 rounded-xl bg-red-50 border border-red-200"
            data-testid="ai-analysis-error"
          >
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
